import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';

import { FootballRivalsCompetitionSortType } from '@sorare/core/src/__generated__/globalTypes';

import { FavoriteCompetitionsTitle } from 'components/rivals/RivalsMatchesDateLayout/FavoriteCompetitionsTitle';
import { LeagueSectionAccordion } from 'components/rivals/RivalsMatchesDateLayout/LeagueSectionAccordion';
import { OrderedSectionTitle } from 'components/rivals/RivalsMatchesDateLayout/OrderedSectionTitle';
import { splitLeaguesByFavorite } from 'lib/rivals';

import { GamesByLeague_footballRivalsLeagueGame } from './__generated__/index.graphql';

type Props<T extends GamesByLeague_footballRivalsLeagueGame['games'][number]> =
  {
    leagueGames: (Omit<GamesByLeague_footballRivalsLeagueGame, 'games'> & {
      games: T[];
    })[];
    renderGame: (game: T) => ReactNode;
    sortBy?: FootballRivalsCompetitionSortType;
    onSortChange?: (sorting: FootballRivalsCompetitionSortType) => void;
  };
export const GamesByLeague = <
  T extends GamesByLeague_footballRivalsLeagueGame['games'][number],
>({
  leagueGames,
  renderGame,
  sortBy,
  onSortChange,
}: Props<T>) => {
  const [favoriteLeagues, orderedLeagues] = splitLeaguesByFavorite(leagueGames);

  const displayFavorites =
    sortBy !== FootballRivalsCompetitionSortType.GAME_DATE &&
    favoriteLeagues.length > 0;
  const leaguesToDisplay = displayFavorites ? orderedLeagues : leagueGames;

  return (
    <>
      {displayFavorites && (
        <section>
          <FavoriteCompetitionsTitle
            onSortChange={onSortChange}
            sortBy={sortBy}
          />
          {favoriteLeagues.map(league => {
            const { games } = league;
            return (
              <LeagueSectionAccordion
                startsOpen
                key={league.competition.slug}
                league={league}
              >
                {games.map(game => (
                  <div key={game.slug}>{renderGame(game)}</div>
                ))}
              </LeagueSectionAccordion>
            );
          })}
        </section>
      )}
      {leaguesToDisplay.length > 0 && (
        <>
          <OrderedSectionTitle onSortChange={onSortChange} sortBy={sortBy} />
          {leaguesToDisplay.map(league => {
            const { games } = league;
            return (
              <LeagueSectionAccordion
                key={league.competition.slug}
                league={league}
              >
                {games.map(game => (
                  <div key={game.slug}>{renderGame(game)}</div>
                ))}
              </LeagueSectionAccordion>
            );
          })}
        </>
      )}
    </>
  );
};

GamesByLeague.fragments = {
  footballRivalsLeagueGame: gql`
    fragment GamesByLeague_footballRivalsLeagueGame on FootballRivalsLeagueGame {
      games {
        slug
      }
      ...splitLeaguesByFavorite_footballRivalsLeagueGame
      ...LeagueSectionAccordion_footballRivalsLeagueGame
    }
    ${splitLeaguesByFavorite.fragments.footballRivalsLeagueGame}
    ${LeagueSectionAccordion.fragments.footballRivalsLeagueGame}
  ` as TypedDocumentNode<GamesByLeague_footballRivalsLeagueGame>,
};
